import React, { useState, useRef, useEffect } from "react";
import {
  FaEdit,
  FaChevronDown,
  FaChevronUp,
  FaSave,
  FaTimes,
  FaPlus,
} from "react-icons/fa";
import {
  GridComponent,
  ColumnDirective,
  ColumnsDirective,
  Inject,
  Page,
  Edit,
  Toolbar,
  Sort,
  Filter,
  Search,
  ColumnMenu,
  CommandColumn,
} from "@syncfusion/ej2-react-grids";
import { db, auth } from "../../firebase";
import {
  getDoc,
  doc,
  collection,
  where,
  getDocs,
  query,
  updateDoc,
} from "firebase/firestore";
import { onAuthStateChanged, getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { TextBoxComponent } from "@syncfusion/ej2-react-inputs";
import DatePickerTemplate from "../../components/DatePickerTemplate";
import { CitizenForm } from "../../components/CitizenForm";
import Modal from "../../components/Modal";
import Popup from "../../components/Popup";
import viewEyeIcon from "../../assets/view-eye.svg";
import { ChevronDown } from "lucide-react";
import ThreeDotMenu from "../../components/ThreeDotMenu";
import AccessAccount from "../AccessAccount";

// Helper functions
const formatPhoneNumber = (number) => {
  if (!number) return "";
  const numberStr = number.toString();
  return (
    <div>
      <span style={{ letterSpacing: "0.01em" }}>
        {"*".repeat(numberStr.length - 4)}
      </span>
      {numberStr.slice(-4)}
    </div>
  );
};

const formatEmail = (email) => {
  if (!email) return "";
  const [localPart, domain] = email.split("@");
  if (!localPart || !domain) return email;
  return (
    <div>
      {localPart[0]}
      <span style={{ letterSpacing: "0.01em" }}>
        {"*".repeat(localPart.length - 2)}
      </span>
      {localPart[localPart.length - 1]}@{domain}
    </div>
  );
};

const fetchPartnerData = async (userId) => {
  try {
    const docRef = doc(db, "users", userId);
    const docc = await getDoc(docRef);

    if (!docc.exists()) {
      console.error("No such document!");
      return null;
    }

    return { id: docc.id, ...docc.data() };
  } catch (error) {
    console.error("Error fetching data: ", error);
    return null;
  }
};

const searchCitizens = async (searchParams) => {
  let { firstName, lastName, birthDate, centerId } = searchParams;

  // Convert search terms to lowercase
  firstName = firstName.toLowerCase();
  lastName = lastName.toLowerCase();

  if (!birthDate) {
    console.log("Missing birth date, returning empty results");
    return [];
  }

  if (!firstName || !lastName || !birthDate || !centerId) {
    console.error("Missing required search parameters");
    return [];
  }

  const usersRef = collection(db, "users");

  // First get all users from the center with matching birth date
  const queryRef = query(
    usersRef,
    where("center_id", "in", centerId),
    where("user_role", "==", "citizen"),
    where("birth_date", "==", birthDate)
  );

  const querySnapshot = await getDocs(queryRef);

  // Filter results client-side for case-insensitive name matching
  const results = querySnapshot.docs
    .filter((doc) => {
      const userData = doc.data();
      return (
        userData.first_name.toLowerCase() === firstName &&
        userData.last_name.toLowerCase() === lastName
      );
    })
    .map((doc) => {
      const userData = doc.data();
      const result = {
        citizen_id: doc.id,
        darci_id: userData.darci_id || userData.username,
        name: `${userData.first_name} ${userData.last_name}`,
        address: `${userData.city}, ${userData.state}`,
        mobile_number: userData.mobile_number,
        email: userData.email,
        birth_date: userData.birth_date,
        has_access_to_rt: userData.has_access_to_rt || [],
      };
      return result;
    });

  return results;
};

const validateFields = (firstName, lastName, birthDate) => {
  const errors = {};

  if (!firstName.trim()) errors.firstName = "First name is required";
  if (!lastName.trim()) errors.lastName = "Last name is required";
  if (!birthDate) errors.birthDate = "Birth date is required";

  return errors;
};

const PartnerCitizenSearch = ({ className = "" }) => {
  const [userId, setUserId] = useState(null);
  const [data, setData] = useState([]);
  const [gridData, setGridData] = useState([]);
  const [loading, setLoading] = useState(true); // NEW: Loading state
  const gridRef = useRef(null);
  const selectedUserId = sessionStorage.getItem("userId");

  const [searchFirstName, setSearchFirstName] = useState("");
  const [searchLastName, setSearchLastName] = useState("");
  const [searchBirthDate, setSearchBirthDate] = useState("");

  const [validationErrors, setValidationErrors] = useState({});
  const [centerId, setCenterId] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [noResultsFound, setNoResultsFound] = useState(false);
  // Add ref for the timeout
  const errorTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [disasterNames, setDisasterNames] = useState([]);
  const [selectedDisaster, setSelectedDisaster] = useState("");
  const [selectedDisasterId, setSelectedDisasterId] = useState("");
  const [isBlueSky, setIsBlueSky] = useState(false);
  const [showAccessAccount, setShowAccessAccount] = useState(false);
  const [accessAccountData, setAccessAccountData] = useState(null);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  // Monitor authentication status
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser && selectedUserId) {
        setUserId(selectedUserId);
      } else {
        setData(null);
      }
      setLoading(false); // End loading after auth state is checked
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (userId) {
      fetchData();
    }
  }, [userId]);

  const mapCitizenData = (citizens) => {
    return citizens.map((citizen) => ({
      name: `${citizen.first_name} ${citizen.last_name}`,
      address: `${citizen.city},${" "} ${citizen.state}`,
      mobile_number: citizen.mobile_number,
      email: citizen.email,
      citizen_id: citizen.citizen_id,
    }));
  };

  const fetchData = async () => {
    const data = await fetchPartnerData(userId);
    if (data) {
      setData(data);
      setCenterId(data.center_id);
    }
  };

  const navigate = useNavigate();

  useEffect(() => {
    const fetchDisasters = async () => {
      try {
        if (!userId || !data?.center_id) return;

        // Get the partner's data to check their counties
        const partnerDoc = await getDoc(doc(db, "partners", data?.partner_id));
        if (!partnerDoc.exists()) {
          console.error("Partner document not found");
          return;
        }

        const partnerData = partnerDoc.data();
        const counties = partnerData?.area_serviced_county || [];

        if (counties.length === 0) {
          return;
        }

        // Query disasters based on the partner's service area
        const disastersRef = collection(db, "declared-disaster");
        const q = query(
          disastersRef,
          where(
            "center_id",
            "in",
            Array.isArray(data.center_id) ? data.center_id : [data.center_id]
          ),
          where("disaster_status", "==", "active"),
          where("is_deleted", "==", false),
          where("affected_counties", "array-contains-any", counties)
        );

        const querySnapshot = await getDocs(q);
        const disasters = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().disaster_name,
        }));

        setDisasterNames(disasters);

        // Set first disaster as default if available
        if (disasters.length > 0) {
          setSelectedDisaster(disasters[0].name);
          setSelectedDisasterId(disasters[0].id);
        }
      } catch (error) {
        console.error("Error fetching disasters:", error);
      }
    };

    fetchDisasters();
  }, [userId, data]);

  const handleViewRecoveryPlan = async ({ rowData }) => {
    try {
      if (!rowData?.citizen_id) return;

      console.log("Saving search state...");
      // Store current search state
      const searchState = {
        firstName: searchFirstName,
        lastName: searchLastName,
        birthDate: searchBirthDate,
        searchResults: gridData,
        isBlueSky: isBlueSky,
      };
      console.log("Search state to save:", searchState);
      sessionStorage.setItem("citizenSearchState", JSON.stringify(searchState));

      // Store recovery tracker data
      sessionStorage.setItem("accessedUserId", rowData.citizen_id);
      sessionStorage.setItem("accessed_case_number", rowData.case_number);
      sessionStorage.setItem("accessed_disaster_id", selectedDisasterId);

      // Navigate to recovery tracker
      navigate("/viewrecovery/irs");
    } catch (error) {
      console.error("Error saving search state:", error);
    }
  };

  const sortSettings = {
    allowSorting: true,
  };

  const filterSettings = {
    type: "CheckBox",
  };
  const columnMenuItems = ["SortAscending", "SortDescending", "Filter"];

  const pageSettings = { pageSize: 10 };
  const [isGridVisible, setIsGridVisible] = useState(true);
  const toggleGrid = (event) => {
    event.preventDefault();
    setIsGridVisible(!isGridVisible);
  };
  const [showPopup, setShowPopup] = useState(false);
  const [showCitizenForm, setShowCitizenForm] = useState(false);
  const handleCommandClick = (args) => {
    const citizenData = args.rowData;
    if (args.commandColumn.type === "View") {
      if (
        citizenData.has_access_to_rt &&
        citizenData.has_access_to_rt.includes(selectedUserId)
      ) {
        handleViewRecoveryPlan({ rowData: citizenData });
      } else {
        setShowPopup(true);
      }
    } else {
      if (
        citizenData.has_access_to_rt &&
        citizenData.has_access_to_rt.includes(selectedUserId)
      ) {
        // Open new tab and set session storage
        const newWindow = window.open("", "_blank");
        newWindow.sessionStorage.setItem("userId", citizenData.citizen_id);
        newWindow.location.href = window.location.origin + "/home";
      } else {
        // Show access account modal for users without access
        setAccessAccountData({
          email: citizenData.email,
          orgUserId: userId,
          orgType: "partner",
          citizen_id: citizenData.citizen_id,
        });
        setShowAccessAccount(true);
      }
    }
  };

  const handleViewAssistanceLog = async (args) => {
    const citizenData = args.rowData;
    localStorage.setItem("citizen_id", citizenData.accessed_userId);
    localStorage.setItem("case_number", citizenData.case_number);
    localStorage.setItem("disaster_id", citizenData.disaster_id);
    navigate("/recovery-tracker");
  };

  const mobileNumberTemplate = (props) =>
    formatPhoneNumber(props.mobile_number);
  const emailTemplate = (props) => formatEmail(props.email);

  const validateSearchFields = () => {
    const errors = validateFields(
      searchFirstName,
      searchLastName,
      searchBirthDate
    );
    setValidationErrors(errors);

    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }

    if (Object.keys(errors).length > 0) {
      errorTimeoutRef.current = setTimeout(() => {
        setValidationErrors({});
      }, 3000);
    }

    return Object.keys(errors).length === 0;
  };

  const handleSearch = async () => {
    if (!validateSearchFields()) return;

    try {
      setLoading(true);
      setNoResultsFound(false);
      const searchParams = {
        firstName: searchFirstName.trim(),
        lastName: searchLastName.trim(),
        birthDate: searchBirthDate,
        centerId,
        ...(isBlueSky ? {} : { disasterId: selectedDisasterId }),
      };

      const results = await searchCitizens(searchParams);

      if (results.length === 0) {
        setNoResultsFound(true);
      }

      // Check if current user ID is in has_access_to_rt array
      const currentUser = auth.currentUser;
      if (currentUser && results.length > 0) {
        const hasAccess =
          results[0].has_access_to_rt?.includes(userId) || false;
      }

      setGridData(results);
    } catch (error) {
      console.error("Error searching users: ", error);
      setGridData([]);
      setNoResultsFound(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchFirstName("");
    setSearchLastName("");
    setSearchBirthDate("");
    setValidationErrors({});
    setGridData([]);

    // Clear any existing timeout
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
  };

  const renderSearchFields = () => {
    return (
      <div className="mb-4 flex flex-wrap items-start justify-between">
        <div className="flex gap-4">
          <div className="flex flex-col">
            <TextBoxComponent
              placeholder="First Name"
              value={searchFirstName}
              change={(e) => {
                if (errorTimeoutRef.current) {
                  clearTimeout(errorTimeoutRef.current);
                }
                setSearchFirstName(e.value);
                setValidationErrors((prev) => ({ ...prev, firstName: "" }));
              }}
              cssClass={`citizen-search-input e-outline ${
                validationErrors.firstName ? "e-error" : ""
              }`}
              floatLabelType="Never"
              width="320px"
              keyUp={(e) => {
                if (e.keyCode === 13) handleSearch();
              }}
            />
            {validationErrors.firstName && (
              <div className="mt-1 text-xs text-red-500">
                {validationErrors.firstName}
              </div>
            )}
          </div>
          <div className="flex flex-col">
            <TextBoxComponent
              placeholder="Last Name"
              value={searchLastName}
              change={(e) => {
                if (errorTimeoutRef.current) {
                  clearTimeout(errorTimeoutRef.current);
                }
                setSearchLastName(e.value);
                setValidationErrors((prev) => ({ ...prev, lastName: "" }));
              }}
              cssClass={`citizen-search-input e-outline ${
                validationErrors.lastName ? "e-error" : ""
              }`}
              floatLabelType="Never"
              width="320px"
              keyUp={(e) => {
                if (e.keyCode === 13) handleSearch();
              }}
            />
            {validationErrors.lastName && (
              <div className="mt-1 text-xs text-red-500">
                {validationErrors.lastName}
              </div>
            )}
          </div>
          <div className="flex flex-col">
            <TextBoxComponent
              placeholder="Birth Date (MM/DD/YYYY)"
              value={searchBirthDate}
              change={(e) => {
                if (errorTimeoutRef.current) {
                  clearTimeout(errorTimeoutRef.current);
                }
                setSearchBirthDate(e.value);
                setValidationErrors((prev) => ({ ...prev, birthDate: "" }));
              }}
              cssClass={`citizen-search-input e-outline ${
                validationErrors.birthDate ? "e-error" : ""
              }`}
              floatLabelType="Never"
              width="320px"
              focus={() => {
                // Show DatePicker when the field is clicked
                const datePickerElement =
                  document.getElementById("birthDatePicker");
                const textBoxElement =
                  document.getElementById("birthDateTextBox");
                if (datePickerElement) {
                  datePickerElement.style.display = "block";
                  datePickerElement.style.width = "280px";
                  datePickerElement.style.borderRadius = "4px";
                  datePickerElement.style.backgroundColor = "white";

                  // Focus the datepicker to ensure it's interactive
                  setTimeout(() => {
                    const datepickerInput =
                      datePickerElement.querySelector("input");
                    if (datepickerInput) {
                      datepickerInput.focus();
                    }
                  }, 100);

                  if (textBoxElement) {
                    textBoxElement.style.display = "none";
                  }
                }
              }}
              keyUp={(e) => {
                if (e.keyCode === 13) handleSearch();
              }}
              id="birthDateTextBox"
            />
            <div
              id="birthDatePicker"
              style={{
                display: "none",
                position: "absolute",
                zIndex: 1000,
                marginTop: "2px",
                marginLeft: "30px",
              }}
            >
              <DatePickerTemplate
                value={searchBirthDate}
                onChange={(val) => {
                  // Format date to ensure leading zeros
                  const formattedDate = val
                    ? new Date(val).toLocaleDateString("en-US", {
                        month: "2-digit",
                        day: "2-digit",
                        year: "numeric",
                      })
                    : "";
                  setSearchBirthDate(formattedDate);
                  setValidationErrors((prev) => ({ ...prev, birthDate: "" }));

                  // Hide datepicker after selection
                  const datePickerElement =
                    document.getElementById("birthDatePicker");
                  const textBoxElement =
                    document.getElementById("birthDateTextBox");
                  if (datePickerElement) {
                    datePickerElement.style.display = "none";
                  }
                  if (textBoxElement) {
                    textBoxElement.style.display = "block";
                  }
                }}
                key={searchBirthDate}
                style={{ width: "250px" }}
                cssClass="e-custom-datepicker"
                enablePersistence={true}
                showClearButton={false}
                strictMode={false}
                openOnFocus={true}
              />
            </div>
            {validationErrors.birthDate && (
              <div className="mt-1 text-xs text-red-500">
                {validationErrors.birthDate}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-end">
          <div className="flex items-start gap-2">
            <button
              type="button"
              className="text-lg h-[56px] w-[200px] cursor-pointer rounded-[4px] bg-[#0a2558] font-semibold text-[#F7CA41] hover:bg-[#0a2558]/90"
              onClick={handleSearch}
            >
              Search
            </button>
            <button
              type="button"
              className="text-lg h-[56px] w-[200px] cursor-pointer rounded-[4px] border border-[#0a2558] bg-[#FFF]   font-semibold text-[#0a2558]"
              onClick={handleClearSearch}
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add handler for form submission success
  const handleFormSuccess = () => {
    setShowCitizenForm(false);
    // Optionally refresh the search results
    handleSearch();
  };

  useEffect(() => {
    const savedSearchState = sessionStorage.getItem("citizenSearchState");
    if (savedSearchState) {
      const { firstName, lastName, birthDate, searchResults } =
        JSON.parse(savedSearchState);
      setSearchFirstName(firstName);
      setSearchLastName(lastName);
      setSearchBirthDate(birthDate);
      setGridData(searchResults);

      // Clear the saved state after restoring it
      sessionStorage.removeItem("citizenSearchState");
    }
  }, []);

  const [activeMenuCitizen, setActiveMenuCitizen] = useState(null);

  return (
    <div
      className={`m-0 box-border flex w-[1648px] max-w-full flex-col items-start justify-start gap-2.5 px-2.5 py-[15px] leading-[normal] tracking-[normal] ${className}`}
    >
      <div className="mb-4 flex w-full items-center justify-between">
        <div className="flex flex-row items-start justify-start gap-1.5 text-xs text-darkslateblue">
          <a className="relative inline-block min-w-[40px] text-left text-xs font-medium leading-[16px] text-dodgerblue [text-decoration:none]">
            Database
          </a>
          <div className="relative inline-block min-w-[5px] text-left font-mulish text-base font-semibold leading-[16px] text-gray-400">
            /
          </div>
          <a className="relative inline-block min-w-[77px] text-left text-xs font-medium leading-[16px] text-dodgerblue [text-decoration:none]">
            Citizen Search
          </a>
        </div>

        {/* Add Citizen Button */}
        {/* <button
          onClick={() => setShowCitizenForm(true)}
          className="cursor-pointer rounded-[4px] bg-[#0a2558] px-9 py-4 text-xl font-medium text-[#F7CA41] hover:bg-[#0a2558]/90"
        >
          <div className="flex items-center justify-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <line x1="19" y1="8" x2="19" y2="14" />
              <line x1="16" y1="11" x2="22" y2="11" />
            </svg>
            Add Citizen
          </div>
        </button> */}
      </div>
      <div id="dialog-container"></div>
      <section className="flex max-w-full flex-col items-start justify-start self-stretch bg-white">
        {/*
        <header className="flex w-full justify-between items-baseline">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-gray-200 transition-all duration-300 ease-in-out hover:bg-gray-300"
              onClick={toggleGrid}
            >
              <span
                className={`transition-transform duration-300 ease-in-out ${
                  isGridVisible ? "rotate-0" : "rotate-180"
                }`}
              >
                <FaChevronDown />
              </span>
            </button>
          </div>
        </header>
        */}
        <p className="relative text-sm font-medium leading-[21px]">
          Partners only have access to citizens that have authorized their
          information to be shared with a partner.
        </p>
        {disasterNames.length > 0 && (
          <div className="my-2 flex items-center gap-4">
            <div className="flex-1 rounded border border-solid border-[#8F8F8F]">
              <div className="" ref={dropdownRef}>
                <div className="relative w-[300px] cursor-pointer rounded px-2">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    disabled={isBlueSky}
                    className={`relative flex w-full cursor-pointer items-center justify-between gap-1.5 bg-transparent px-0 py-2 text-[17px] ${
                      isBlueSky ? "text-gray-400" : "text-smokey-grey"
                    }`}
                  >
                    <span className="truncate">
                      {selectedDisaster
                        ? selectedDisaster
                        : "Select a Disaster"}
                    </span>
                    <ChevronDown className="h-4 w-4" strokeWidth={2} />
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute z-10 -ml-2 mt-1 w-[300px] rounded border border-gray-300 bg-white shadow-lg">
                      <div className="max-h-60 overflow-y-auto">
                        {disasterNames.length > 0 ? (
                          disasterNames.map((disaster) => (
                            <div
                              key={disaster.id}
                              className="hover:bg-gray-100 cursor-pointer px-4 py-2"
                              onClick={() => {
                                setSelectedDisasterId(disaster.id);
                                setSelectedDisaster(disaster.name);
                                setIsDropdownOpen(false);
                              }}
                            >
                              {disaster.name}
                            </div>
                          ))
                        ) : (
                          <div className="px-4 py-2 text-gray-500">
                            No active disasters available
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Add BlueSky checkbox */}
            <div className="flex items-center gap-2">
              <div className="relative flex items-center">
                <input
                  type="checkbox"
                  checked={isBlueSky}
                  onChange={(e) => setIsBlueSky(e.target.checked)}
                  className="peer h-6 w-6 cursor-pointer appearance-none rounded-[2px] border-2 border-[#003460] bg-white shadow-[inset_0_0_0_1px_#003460] checked:border-[#003460] checked:bg-[#003460] checked:shadow-none"
                />
                <svg
                  className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 peer-checked:opacity-100"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.3334 4L6.00008 11.3333L2.66675 8"
                    stroke="#F7CA41"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span className="text-base font-medium text-[#242424]">
                BlueSky
              </span>
            </div>
          </div>
        )}
        <section className="flex max-w-full flex-col items-start justify-start self-stretch bg-white">
          <div>
            {renderSearchFields()}

            {loading ? (
              <div className="flex flex-col items-center justify-center p-8">
                <p className="text-gray-600 mb-4 text-center">
                  Searching for Results....
                </p>
                <div className="h-2 w-full max-w-3xl rounded-full bg-gray-200">
                  <div className="h-full w-1/2 animate-pulse rounded-full bg-blue-600"></div>
                </div>
              </div>
            ) : (
              <GridComponent
                ref={gridRef}
                dataSource={gridData}
                allowSorting={true}
                sortSettings={sortSettings}
                filterSettings={filterSettings}
                allowFiltering={true}
                allowPaging={true}
                pageSettings={pageSettings}
                showColumnMenu={true}
                columnMenuItems={columnMenuItems}
                commandClick={handleCommandClick}
                allowSelection={true}
                selectionSettings={{ type: "Single" }}
                cssClass="new-grid-color allow-overflow-grid"
              >
                <ColumnsDirective>
                  <ColumnDirective
                    field="darci_id"
                    headerText="Darci ID"
                    width="200"
                  />
                  <ColumnDirective field="name" headerText="Name" width="200" />
                  <ColumnDirective
                    field="mobile_number"
                    headerText="Mobile Number"
                    width="180"
                    template={mobileNumberTemplate}
                  />
                  <ColumnDirective
                    field="email"
                    headerText="Email Address"
                    width="250"
                    template={emailTemplate}
                  />
                  <ColumnDirective
                    field="address"
                    headerText="Location"
                    width="150"
                  />

                  <ColumnDirective
                    headerText="Actions"
                    textAlign="Center"
                    width="100"
                    template={(props) => {
                      return (
                        <div className="flex w-full justify-center overflow-visible px-4">
                          <ThreeDotMenu
                            menuId={props?.citizen_id}
                            activeMenuId={activeMenuCitizen}
                            setActiveMenuId={setActiveMenuCitizen}
                            clickOutside={false}
                            customItems={[
                              {
                                label: "View Recovery Tracker",
                                onClick: (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setActiveMenuCitizen(null);
                                  if (
                                    props.has_access_to_rt &&
                                    Array.isArray(props.has_access_to_rt) &&
                                    props.has_access_to_rt.includes(
                                      selectedUserId
                                    )
                                  ) {
                                    handleViewRecoveryPlan({ rowData: props });
                                  } else {
                                    setShowPopup(true);
                                  }
                                },
                              },
                              {
                                label: "Access Account",
                                onClick: (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setActiveMenuCitizen(null);

                                  if (
                                    props.has_access_to_rt &&
                                    Array.isArray(props.has_access_to_rt) &&
                                    props.has_access_to_rt.includes(
                                      selectedUserId
                                    )
                                  ) {
                                    // Open new tab and set session storage
                                    const newWindow = window.open("", "_blank");
                                    newWindow.sessionStorage.setItem(
                                      "userId",
                                      props.citizen_id
                                    );
                                    newWindow.location.href =
                                      window.location.origin + "/home";
                                  } else {
                                    // Show access account modal for users without access
                                    setAccessAccountData({
                                      email: props.email,
                                      orgUserId: userId,
                                      orgType: "partner",
                                      citizen_id: props.citizen_id,
                                    });
                                    setShowAccessAccount(true);
                                  }
                                },
                              },
                            ]}
                          />
                        </div>
                      );
                    }}
                  />
                </ColumnsDirective>
                <Inject
                  services={[
                    Page,
                    Edit,
                    Toolbar,
                    Sort,
                    Filter,
                    ColumnMenu,
                    CommandColumn,
                  ]}
                />
              </GridComponent>
            )}
          </div>
        </section>
      </section>
      {showPopup && (
        <Popup
          title="Access Denied"
          message="This citizen has not authorized access to their recovery tracker."
          onClose={() => setShowPopup(false)}
        />
      )}
      {noResultsFound && (
        <Popup
          title="No Results Found"
          message="No citizens found matching your search criteria. Please try again with different information."
          onClose={() => setNoResultsFound(false)}
        />
      )}
      {/* Citizen Form Modal */}
      {showCitizenForm && (
        <Modal
          isOpen={showCitizenForm}
          onClose={() => setShowCitizenForm(false)}
          title="Add New Citizen"
          width="80%"
          height="85%"
          noFooter={true}
        >
          <CitizenForm
            onSubmitSuccess={handleFormSuccess}
            centerState={data?.state}
            centerCounty={data?.county}
            centerId={centerId}
            centerEmail={data?.email}
            fromCitizenSearch={true}
          />
        </Modal>
      )}
      <AccessAccount
        isOpen={showAccessAccount}
        onClose={() => {
          setShowAccessAccount(false);
          setAccessAccountData(null);
        }}
        citizenData={accessAccountData}
        orgType={"partner"}
      />
    </div>
  );
};

export default PartnerCitizenSearch;
